import { useEffect, useState } from 'react';
import { Box, Dialog, DialogContent, IconButton, TablePagination, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';

export interface ImportListedRow {
    line: number;
    name: string;
    phone: string;
    /// O porquê da linha estar na lista: quem já é no CRM, a coluna do lead, a linha que repete.
    detail?: string | null;
}

interface Props {
    open: boolean;
    title: string;
    /// Cabeçalho da coluna de detalhe; sem ele a coluna não aparece.
    detailLabel?: string;
    rows: ImportListedRow[];
    onClose: () => void;
}

/// Quem está atrás de um contador da conferência, no mesmo formato da tabela de atendentes do painel.
export const ImportRowsDialog = ({ open, title, detailLabel, rows, onClose }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    // Cada contador abre a própria lista: começa da primeira página, não de onde a anterior parou.
    useEffect(() => {
        if (open) setPage(0);
    }, [open, rows]);

    const visible = rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

    const cell = {
        padding: '8px 10px',
        fontSize: '0.8rem',
        borderBottom: `1px solid ${cc.border}`,
        color: cc.textPrimary,
    };

    const head = {
        ...cell,
        textAlign: 'left' as const,
        color: cc.textSecondary,
        textTransform: 'uppercase' as const,
        letterSpacing: '.06em',
        fontSize: '10px',
        fontWeight: 700,
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { background: cc.cardBg, backgroundImage: 'none' } }}
        >
            <DialogContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={700} sx={{ color: cc.textPrimary }}>
                            {title}
                        </Typography>
                        <Typography variant="caption" sx={{ color: cc.textSecondary }}>
                            {t('people.importar.linhasNaLista', { count: rows.length })}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={onClose} aria-label={t('general.fechar')} sx={{ color: cc.textSecondary }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{ width: '100%', minWidth: 460, borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <Box component="th" sx={{ ...head, width: '10%' }}>{t('people.importar.colunaLinha')}</Box>
                                <Box component="th" sx={{ ...head, width: detailLabel ? '35%' : '55%' }}>
                                    {t('general.nome')}
                                </Box>
                                <Box component="th" sx={{ ...head, width: '25%' }}>{t('people.importar.colunaTelefone')}</Box>
                                {detailLabel && <Box component="th" sx={{ ...head, width: '30%' }}>{detailLabel}</Box>}
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((row) => (
                                <tr key={row.line}>
                                    <Box component="td" sx={{ ...cell, color: cc.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                                        {row.line}
                                    </Box>
                                    <Box component="td" sx={{ ...cell, fontWeight: 600 }}>{row.name || '—'}</Box>
                                    <Box component="td" sx={{ ...cell, fontVariantNumeric: 'tabular-nums' }}>
                                        {row.phone || '—'}
                                    </Box>
                                    {detailLabel && (
                                        <Box component="td" sx={{ ...cell, color: cc.textSecondary }}>{row.detail || '—'}</Box>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </Box>
                </Box>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    labelRowsPerPage={t('general.linhasPorPagina')}
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} ${t('general.X-de-X')} ${count !== -1 ? count : `${t('general.maisQue')} ${to}`}`
                    }
                    component="div"
                    count={rows.length}
                    page={page}
                    onPageChange={(_, next) => setPage(next)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value));
                        setPage(0);
                    }}
                />
            </DialogContent>
        </Dialog>
    );
};
